import { useEffect, useState } from 'react'
import { trackUse } from '@/state/usage'
import { useSearch } from '@tanstack/react-router'
import { podWords } from '@/lib/podWords'
import { ChevronRight } from 'lucide-react'
import { api } from '@/runtime'
import type { Build, ClusterRunner, ClusterService, ClusterSummary, Release } from '@/db/api'
import { useNow } from '@/hooks/useNow'
import { fmtAgo } from '@/lib/time'
import type { Tone } from '@/lib/colour'
import { useUi } from '@/state/ui'
import { Skeleton } from '@/components/Skeleton'
import { HealthDot } from '@/components/ClusterBits'
import { ShipPipeline, useBuilds } from '@/components/ShipPipeline'
import { pipelineOf, shipping } from '@/lib/pipeline'
import { Button, Empty, Failed, PageTitle, SectionTitle, Sheet, Tag, cx } from '@/components/ui'

/**
 * Releases (docs/shipping.md): every commit on main is built by the factory in
 * the cluster and staging follows by itself; prod moves only from here (or a
 * card Prime asks with), by tagging a built image for prod. Releasing an older
 * build again is a rollback. Online only. Under the builds, what makes them:
 * the runners and the image registry (restarts and the last exit; from the old
 * Infrastructure page, 2026-10-10).
 */

const buildTone = (s: Build['state']): Tone =>
  s === 'built' ? 'done' : s === 'failed' ? 'warn' : s === 'building' ? 'running' : 'muted'

const stateWords: Record<Build['state'], string> = {
  queued: 'Queued',
  building: 'Building',
  built: 'Built',
  failed: 'Failed',
}

/** The sheet that ships a build to prod, once the owner says so. */
export const ShipSheet = ({
  sha,
  note,
  back,
  onClose,
  onDone,
}: {
  sha: string
  note?: string
  back: boolean
  onClose: () => void
  onDone: () => void
}) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ship = async () => {
    setBusy(true)
    setError('')
    try {
      await api.release(sha)
      trackUse('action:ship')
      useUi.getState().showFlash(`Released ${sha}. Prod picks it up within a few minutes.`, 5000)
      onDone()
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title={back ? 'Roll prod back?' : 'Ship to prod?'}
      footer={
        <div className="flex gap-2 justify-end">
          <Button onClick={onClose}>Not yet</Button>
          <Button variant="primary" disabled={busy} onClick={() => void ship()} data-testid="release-confirm">
            {back ? 'Roll back' : 'Ship it'}
          </Button>
        </div>
      }
    >
      <p className="text-[15px]">
        <span className="font-mono">{sha}</span>
        {note ? <> · {note}</> : null}
      </p>
      <p className="text-sm text-muted mt-2">
        The image staging ran is tagged for prod; prod restarts on it within a few minutes, with a few seconds
        offline.
      </p>
      {error && <p className="text-sm text-rose-300 mt-2">{error}</p>}
    </Sheet>
  )
}

/** One build's log, fetched when opened. */
const LogSheet = ({
  build,
  onClose,
  onRetry,
}: {
  build: Build
  onClose: () => void
  onRetry: () => void
}) => {
  const [log, setLog] = useState<string | null>(null)
  useEffect(() => {
    let stale = false
    api
      .buildLog(build.name)
      .then((r) => !stale && setLog(r.log || 'No log (the pod is gone).'))
      .catch((e: Error) => !stale && setLog(e.message))
    return () => {
      stale = true
    }
  }, [build.name])
  return (
    <Sheet
      open
      onClose={onClose}
      title={`${build.tag} · ${stateWords[build.state]}`}
      footer={
        build.state === 'failed' ? (
          <div className="flex justify-end">
            <Button variant="primary" onClick={onRetry}>
              Build it again
            </Button>
          </div>
        ) : undefined
      }
    >
      {build.note && <p className="text-[15px] mb-2">{build.note}</p>}
      {build.error && <p className="text-sm text-rose-300 mb-2">{build.error}</p>}
      {log === null ? (
        <Skeleton className="h-40" />
      ) : (
        <pre className="text-[11px] leading-4 whitespace-pre-wrap break-all bg-raised rounded-[var(--radius-card-inner)] p-2 max-h-[50vh] overflow-auto">
          {log}
        </pre>
      )}
    </Sheet>
  )
}

const RUNNER_KIND: Record<ClusterRunner['kind'], string> = {
  forgejo: 'Forgejo runner',
  github: 'GitHub runner',
  build: 'Image build',
}

/** CI and build pods, from the cluster summary: is each up, where, since when. */
export const Runners = ({ runners, now }: { runners: ClusterRunner[]; now: number }) => (
  <ul className="group-list" data-testid="runners">
    {runners.map((r) => {
      const up = r.ready || r.phase === 'Succeeded'
      return (
        <li key={`${r.namespace}/${r.name}`} className="flex items-center gap-3 px-3 py-2.5 min-h-12">
          <HealthDot health={up ? 'ok' : r.phase === 'Failed' ? 'warn' : 'off'} />
          <span className="flex-1 min-w-0">
            <span className="block leading-5">{RUNNER_KIND[r.kind]}</span>
            <span className="block font-mono text-[11px] text-faint truncate">
              {r.node ? `${r.node}  ` : ''}
              {r.image?.split('/').pop() ?? r.name}
            </span>
          </span>
          <span className="text-right shrink-0">
            <span className="block text-[12px] text-muted">
              {r.phase === 'Running' ? (r.ready ? 'Ready' : 'Starting') : r.phase}
            </span>
            <span className="block text-[11px] text-faint">
              {r.since ? fmtAgo(r.since, now).replace(' ago', '') : ''}
              {r.restarts ? `, ${r.restarts} restart${r.restarts === 1 ? '' : 's'}` : ''}
            </span>
          </span>
        </li>
      )
    })}
  </ul>
)

/** The image registry's pods: up since when, restarts and the last exit's reason. */
export const Registry = ({ pods, now }: { pods: ClusterService[]; now: number }) => (
  <ul className="group-list" data-testid="registry">
    {pods.map((r) => {
      const up = r.since ? fmtAgo(r.since, now).replace(' ago', '') : ''
      return (
        <li key={r.namespace + '/' + r.name} className="flex items-center gap-3 px-3 py-2.5 min-h-12">
          <HealthDot health={r.ready ? 'ok' : 'warn'} />
          <span className="flex-1 min-w-0">
            <span className="block leading-5">
              {!r.ready
                ? r.phase === 'Running'
                  ? 'Not ready'
                  : r.phase
                : !up
                  ? 'Up'
                  : up === 'just now'
                    ? 'Up, just started'
                    : `Up for ${up}`}
            </span>
            <span className="block font-mono text-[11px] text-faint truncate">
              {r.node ? `${r.node}  ` : ''}
              {r.name}
            </span>
          </span>
          <span className="text-right shrink-0" data-testid="registry-restarts">
            <span className="block text-[12px] text-muted">
              {r.restarts === 0 ? 'No restarts' : `${r.restarts} restart${r.restarts === 1 ? '' : 's'}`}
            </span>
            {r.last_exit && (
              <span className="block text-[11px] text-faint">
                Last stopped: {podWords(r.last_exit)}
                {r.last_exit_at ? `, ${fmtAgo(r.last_exit_at, now)}` : ''}
              </span>
            )}
          </span>
        </li>
      )
    })}
  </ul>
)

/**
 * Where a build stands, in one chip: the furthest it has gone (prod, on its way
 * there, staging), else its own state; a built one nobody runs needs no chip.
 * Never two at once: a build is not building and on prod.
 */
export const buildChip = (
  b: Pick<Build, 'state'>,
  where: { onProd: boolean; onWay?: boolean; onStaging: boolean },
): { word: string; tone: Tone } | undefined => {
  if (b.state !== 'built') return { word: stateWords[b.state], tone: buildTone(b.state) }
  if (where.onProd) return { word: 'On prod', tone: 'accent' }
  if (where.onWay) return { word: 'On its way', tone: 'running' }
  if (where.onStaging) return { word: 'On staging', tone: 'muted' }
  return undefined
}

const BuildRow = ({
  b,
  ago,
  onProd,
  onWay,
  onStaging,
  onOpen,
  onShip,
}: {
  b: Build
  ago: string
  onProd: boolean
  onWay?: boolean
  onStaging: boolean
  onOpen: () => void
  onShip?: () => void
}) => {
  const chip = buildChip(b, { onProd, onWay, onStaging })
  return (
    <li className="flex items-center gap-2 px-3 py-2.5 min-h-14">
      <button type="button" onClick={onOpen} className="flex-1 min-w-0 text-left">
        <span className="block leading-5 line-clamp-2">{b.note || b.tag || b.sha.slice(0, 12)}</span>
        <span className="flex flex-wrap items-center gap-1.5 mt-1">
          {chip && <Tag tone={chip.tone}>{chip.word}</Tag>}
          <span className="font-mono text-[11px] text-faint">{b.tag || b.sha.slice(0, 12)}</span>
          <span className="text-[11px] text-faint">{ago}</span>
        </span>
      </button>
      {onShip && <Button onClick={onShip}>Ship</Button>}
    </li>
  )
}

export const ReleasesView = () => {
  const now = useNow(30_000)
  const { data, error, unreachable, reload } = useBuilds()
  const [cluster, setCluster] = useState<ClusterSummary | null>(null)
  const [ship, setShip] = useState<{ sha: string; note?: string; back: boolean } | null>(null)
  const [chosen, setOpen] = useState<Build | null>(null)
  // ?build=<name> (the graph's link to a build) opens that build's log once it is listed
  const { build: wanted } = useSearch({ strict: false }) as { build?: string }
  const [wantedClosed, setWantedClosed] = useState(false)
  const open =
    chosen ?? (!wantedClosed && wanted ? (data?.builds.find((x) => x.name === wanted) ?? null) : null)
  const closeLog = () => {
    setOpen(null)
    setWantedClosed(true)
  }
  // the runners and the registry are best effort: off the cluster (dev) there are none to show
  useEffect(() => {
    let stale = false
    const load = () =>
      api
        .cluster()
        .then((c) => !stale && setCluster(c))
        .catch(() => {})
    void load()
    const t = window.setInterval(() => document.visibilityState === 'visible' && void load(), 30_000)
    return () => {
      stale = true
      window.clearInterval(t)
    }
  }, [])
  const retry = async (b: Build) => {
    closeLog()
    try {
      await api.buildStart(b.sha)
      reload()
    } catch (e) {
      useUi.getState().showFlash((e as Error).message, 4000)
    }
  }
  const prod = data?.versions.prod ?? ''
  const staging = data?.versions.staging ?? ''
  // an environment runs a build when their versions agree; a build with no tag yet (one
  // under way) is matched by its commit, never by an empty string that every version starts with
  const runs = (env: string, tag: string) => !!env && !!tag && (env.startsWith(tag) || tag.startsWith(env))
  const runsBuild = (env: string, b: Build) => runs(env, b.tag) || runs(env, b.sha.slice(0, 12))
  // the release prod runs (by what it answers), not the newest tag, which may be on its way
  const live = data?.releases.find((r) => runs(prod, r.sha))
  const ago = (iso?: string) => (iso ? fmtAgo(iso, now) : '')
  const builds = data?.builds ?? []
  // builds come newest first, so everything after prod's own build is already in prod
  const prodAt = builds.findIndex((b) => runsBuild(prod, b))
  const ahead = prodAt < 0 ? builds : builds.slice(0, prodAt)
  const shipped = prodAt < 0 ? [] : builds.slice(prodAt)
  // each card's words are its own version's commit
  const noteOf = (v: string) => builds.find((b) => runsBuild(v, b))?.note
  // a release on its way: it and every build older than it are going out, not to ship again
  const onWay = shipping(data) ? (data?.releases[0]?.sha ?? '') : ''
  const onWayAt = onWay ? builds.findIndex((b) => runsBuild(onWay, b)) : -1
  const goingOut = (b: Build) => onWayAt >= 0 && builds.findIndex((x) => x.name === b.name) >= onWayAt
  const outgoing = data?.outgoing && !goingOut(data.outgoing.build) ? data.outgoing : undefined
  const [olderOpen, setOlderOpen] = useState(false)
  const [allReleases, setAllReleases] = useState(false)
  const releases = data ? (allReleases ? data.releases : data.releases.slice(0, 4)) : []
  return (
    <div className="max-w-2xl" data-testid="releases">
      <PageTitle>Releases</PageTitle>
      {error && !data && <Failed what="the builds" reason={error} onRetry={reload} />}
      {!data && !error && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-40" />
        </div>
      )}
      {data && (
        <>
          <section className="mb-5">
            <SectionTitle>Running now</SectionTitle>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['Prod', prod, live ? `Released ${ago(live.at)}` : ''],
                  [
                    'Staging',
                    staging,
                    data.outgoing ? 'Not on prod yet' : runs(prod, staging) ? 'Same as prod' : '',
                  ],
                ] as const
              ).map(([name, v, sub]) => (
                <div
                  key={name}
                  className="card px-3 py-2.5 min-w-0"
                  data-testid={`env-${name.toLowerCase()}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cx('h-2 w-2 rounded-full', v ? 'bg-emerald-400' : 'bg-amber-400')}
                      aria-hidden
                    />
                    <span className="label">{name}</span>
                    <span className="ml-auto font-mono text-[11px] text-faint">{v || 'not answering'}</span>
                  </div>
                  <p className="text-[13px] leading-snug mt-1.5 line-clamp-2">{noteOf(v) ?? '\u00a0'}</p>
                  {sub && <p className="text-[11px] text-faint mt-1">{sub}</p>}
                </div>
              ))}
            </div>
          </section>
          {data && pipelineOf(data, now, unreachable) && (
            <section className="mb-5">
              <ShipPipeline builds={data} unreachable={unreachable} />
            </section>
          )}
          {outgoing && (
            <section className="mb-5" data-testid="outgoing">
              <SectionTitle>Waiting for prod</SectionTitle>
              <div className="card p-3">
                <ul className="flex flex-col gap-1.5 mb-3">
                  {(outgoing.commits.length
                    ? outgoing.commits
                    : [
                        {
                          sha: outgoing.build.sha,
                          subject: outgoing.build.note || outgoing.build.tag,
                        },
                      ]
                  ).map((c) => (
                    <li key={c.sha} className="text-sm leading-5 flex gap-2">
                      <span className="font-mono text-[11px] text-faint pt-px shrink-0">
                        {c.sha.slice(0, 7)}
                      </span>
                      <span>{c.subject}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant="primary"
                  big
                  className="w-full"
                  onClick={() =>
                    setShip({
                      sha: outgoing.build.tag,
                      // named by its newest change, never counted
                      note: outgoing.commits[0]?.subject || outgoing.build.note,
                      back: false,
                    })
                  }
                  data-testid="ship-all"
                >
                  Ship all to prod
                </Button>
              </div>
            </section>
          )}
          <section className="mb-5">
            <SectionTitle>Builds of main</SectionTitle>
            {builds.length === 0 ? (
              <Empty>Nothing built in the last three days. Every merge into main builds by itself.</Empty>
            ) : (
              <ul className="group-list" data-testid="builds">
                {ahead.map((b) => (
                  <BuildRow
                    key={b.name}
                    b={b}
                    ago={ago(b.finished || b.started)}
                    onProd={false}
                    onWay={goingOut(b)}
                    onStaging={runsBuild(staging, b)}
                    onOpen={() => setOpen(b)}
                    onShip={
                      b.state === 'built' && !goingOut(b)
                        ? () => setShip({ sha: b.tag, note: b.note, back: false })
                        : undefined
                    }
                  />
                ))}
                {shipped.slice(0, 1).map((b) => (
                  <BuildRow
                    key={b.name}
                    b={b}
                    ago={ago(b.finished || b.started)}
                    onProd
                    onStaging={runsBuild(staging, b)}
                    onOpen={() => setOpen(b)}
                  />
                ))}
                {shipped.length > 1 && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setOlderOpen((v) => !v)}
                      aria-expanded={olderOpen}
                      className="w-full flex items-center gap-2 px-3 min-h-11 text-[13px] text-muted hover:text-fg"
                      data-testid="builds-older"
                    >
                      <ChevronRight
                        size={14}
                        className={cx('transition-transform', olderOpen && 'rotate-90')}
                        aria-hidden
                      />
                      {shipped.length - 1} earlier {shipped.length === 2 ? 'build' : 'builds'}, all in prod
                    </button>
                  </li>
                )}
                {olderOpen &&
                  shipped
                    .slice(1)
                    .map((b) => (
                      <BuildRow
                        key={b.name}
                        b={b}
                        ago={ago(b.finished || b.started)}
                        onProd={false}
                        onStaging={false}
                        onOpen={() => setOpen(b)}
                      />
                    ))}
              </ul>
            )}
          </section>
          {!!cluster?.runners?.length && (
            <section className="mb-5">
              <SectionTitle>Runners</SectionTitle>
              <Runners runners={cluster.runners} now={now} />
            </section>
          )}
          {!!cluster?.registry?.length && (
            <section className="mb-5">
              <SectionTitle>Registry</SectionTitle>
              <Registry pods={cluster.registry} now={now} />
            </section>
          )}
          {data.releases.length > 0 && (
            <section className="mb-5">
              <SectionTitle>Released to prod</SectionTitle>
              <ul className="group-list">
                {releases.map((r: Release, i) => (
                  <li key={r.tag} className="flex items-center gap-2 px-3 min-h-12">
                    <span className="font-mono text-sm flex-1 min-w-0 truncate">{r.sha}</span>
                    <span className="text-[11px] text-faint">{ago(r.at)}</span>
                    {r === live ? (
                      <Tag tone="accent">Live</Tag>
                    ) : i === 0 && onWay ? (
                      <Tag tone="running">On its way</Tag>
                    ) : runs(prod, r.sha) ? null : (
                      <Button variant="quiet" onClick={() => setShip({ sha: r.sha, back: true })}>
                        Roll back
                      </Button>
                    )}
                  </li>
                ))}
                {data.releases.length > 4 && !allReleases && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setAllReleases(true)}
                      className="w-full text-left px-3 min-h-11 text-[13px] text-muted hover:text-fg"
                    >
                      {data.releases.length - 4} older releases
                    </button>
                  </li>
                )}
              </ul>
            </section>
          )}
        </>
      )}
      {ship && (
        <ShipSheet
          {...ship}
          onClose={() => setShip(null)}
          onDone={() => {
            setShip(null)
            reload()
          }}
        />
      )}
      {open && <LogSheet build={open} onClose={closeLog} onRetry={() => void retry(open)} />}
    </div>
  )
}
